import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertTriangle,
  Pill,
  ImageIcon,
  Link2,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgram,
  DisplayMode,
  clinicProgramService,
} from '@/services/clinicProgramService'
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog'
import ClinicProgramFormDialog from '@/components/dialog/ClinicProgramFormDialog'
import ClinicAssignProductsDialog from '@/components/dialog/ClinicAssignProductsDialog'
import ClinicDefaultMedicationsDialog from '@/components/dialog/ClinicDefaultMedicationsDialog'
import ClinicProgramImageDialog from '@/components/dialog/ClinicProgramImageDialog'
import DisplayModeToggle from '@/components/clinic-programs/DisplayModeToggle'

const colorPalette = [
  { bg: 'bg-[#94D2BD]', text: 'text-[#2D6A5D]', accent: 'from-[#94D2BD]/10' },
  { bg: 'bg-[#B8B8FF]', text: 'text-[#5D5DA8]', accent: 'from-[#B8B8FF]/10' },
  { bg: 'bg-[#FFD6A5]', text: 'text-[#9A7B4F]', accent: 'from-[#FFD6A5]/10' },
  { bg: 'bg-[#FFADAD]', text: 'text-[#A65D5D]', accent: 'from-[#FFADAD]/10' },
  { bg: 'bg-[#A0C4FF]', text: 'text-[#5075A8]', accent: 'from-[#A0C4FF]/10' },
  { bg: 'bg-[#CAFFBF]', text: 'text-[#5A8A4F]', accent: 'from-[#CAFFBF]/10' },
  { bg: 'bg-[#A99BC9]', text: 'text-[#6B5B8A]', accent: 'from-[#A99BC9]/10' },
  { bg: 'bg-[#FFC6FF]', text: 'text-[#8A5D8A]', accent: 'from-[#FFC6FF]/10' },
]

const getColorForIndex = (index: number) => colorPalette[index % colorPalette.length]

interface ClinicProgramCardProps {
  program: ClinicProgram
  index: number
  clinicId: string
  onSuccess: () => void
  clinicArchetype?: 'guided' | 'direct' | 'hybrid' | null
}

const ClinicProgramCard = ({
  program,
  index,
  clinicId,
  onSuccess,
  clinicArchetype,
}: ClinicProgramCardProps) => {
  const [show, setShow] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [productOpen, setProductOpen] = useState(false)
  const [defaultMedsOpen, setDefaultMedsOpen] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const colors = getColorForIndex(index)
  const { toast } = useToast()

  const getInitials = (title: string) => title?.charAt(0)?.toUpperCase() || 'P'

  const handleDelete = async () => {
    try {
      await clinicProgramService.deleteProgram(clinicId, program.id)
      onSuccess()
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to delete program',
        variant: 'destructive',
      })
      throw error
    }
  }

  const handleDisplayModeUpdate = async (mode: DisplayMode) => {
    try {
      await clinicProgramService.updateProgram(clinicId, program.id, { display_mode: mode })
      toast({
        title: 'Updated',
        description: `Program routing changed to ${mode === 'protocol_showcase' ? 'Nimbus-Routed' : 'In-house'}`,
        variant: 'success',
      })
      onSuccess()
    } catch {
      toast({ title: 'Error', description: 'Failed to change program routing', variant: 'destructive' })
    }
  }

  return (
    <>
      <Card className="group h-full bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300 overflow-hidden relative">
        <div className={`absolute top-0 left-0 right-0 h-1 ${colors.bg}`} />
        <div className={`absolute inset-0 bg-gradient-to-br ${colors.accent} to-transparent opacity-50`} />

        {clinicArchetype === 'hybrid' && (
          <div className='relative flex items-center justify-end px-4 pt-3'>
            <DisplayModeToggle
              currentMode={program.display_mode ?? 'medication_select'}
              programId={program.id}
              clinicId={clinicId}
              programTitle={program.title}
              onUpdate={handleDisplayModeUpdate}
            />
          </div>
        )}

        <CardHeader className="pb-3 relative">
          <div className="flex items-start gap-4">
            {program?.image_url ? (
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-300 border border-slate-100">
                <img src={program.image_url} alt={program.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
                <span className={`${colors.text} font-semibold text-lg font-montserrat`}>
                  {getInitials(program.title)}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0 pt-1">
              <CardTitle className="text-base font-semibold text-slate-900 group-hover:text-slate-800 transition-colors line-clamp-1">
                {program?.title || '--'}
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-1 line-clamp-1">
                {program?.subtitle || 'No subtitle'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0 relative space-y-4">
          {program?.program_products?.length === 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                No products assigned.
              </p>
            </div>
          )}

          {program?.program_products?.length > 0 && (
            <div className="rounded-lg border bg-slate-50/50 border-slate-100 overflow-hidden">
              <button
                onClick={() => setShow(show === program.id ? '' : program.id)}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-slate-600">{program.program_products.length}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-700">Products Assigned</span>
                </div>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${show === program.id ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {show === program.id && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-100/50 bg-white/30">
                  <div className="flex flex-wrap gap-1.5">
                    {program?.program_products?.map((pp, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-white border border-slate-200 text-slate-600"
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${colors.bg}`} />
                        {pp?.product?.description || 'Unknown product'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center pt-3 border-t border-slate-100 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium border-slate-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700"
              onClick={() => setImageDialogOpen(true)}
            >
              <ImageIcon className="h-3.5 w-3.5" />
              Image
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40"
              disabled={program?.program_products?.length === 0}
              onClick={() => setDefaultMedsOpen(true)}
            >
              <Pill className="h-3.5 w-3.5" />
              Defaults
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700"
              onClick={() => setProductOpen(true)}
            >
              <Link2 className="h-3.5 w-3.5" />
              Products
            </Button>
          </div>
        </CardContent>
      </Card>

      <ClinicProgramFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        clinicId={clinicId}
        mode="edit"
        program={program}
        onSuccess={onSuccess}
      />
      <ClinicAssignProductsDialog
        open={productOpen}
        onOpenChange={setProductOpen}
        clinicId={clinicId}
        program={program}
        onSuccess={onSuccess}
      />
      <ClinicDefaultMedicationsDialog
        open={defaultMedsOpen}
        onOpenChange={setDefaultMedsOpen}
        clinicId={clinicId}
        program={program}
        onSuccess={onSuccess}
      />
      <ClinicProgramImageDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        clinicId={clinicId}
        program={program}
        onSuccess={onSuccess}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        itemName={program.title}
        itemType="program"
        title="Remove Program"
        description={`Are you sure you want to remove "${program.title}"?`}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default ClinicProgramCard
